import "./simple-pdf-tool.css";
import "./pdf-crop.css";
import { cropFromInsets, croppedPdfName, FULL_CROP, insetsFromCrop, normalizeCrop } from "./pdf-crop-core.js";
import { cropPdf } from "./pdf-crop-export.js";
import { openBrowserPdf, pdfErrorMessage, renderPageIntoCanvas } from "./pdf-browser-core.js";
import { createOperationResult } from "./operation-result.js";

const root = document.querySelector("[data-pdf-crop]");

if (root) {
  const ids = ["cropStatus", "cropUploadPanel", "cropDropZone", "chooseCropPdf", "cropFileInput", "cropWorkspace", "cropFileName", "cropFileDetails", "replaceCropPdf", "cropCanvas", "cropSurface", "cropSelection", "cropPrevious", "cropNext", "cropPage", "cropPageCount", "cropTop", "cropRight", "cropBottom", "cropLeft", "applyCropAll", "resetCrop", "downloadCroppedPdf", "cropProgress"];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const state = { file: null, bytes: null, pdf: null, page: 1, crops: new Map(), busy: false, dragging: null };
  const operationResult = createOperationResult(root, elements.cropWorkspace);

  const showStatus = (message = "") => { elements.cropStatus.textContent = message; elements.cropStatus.hidden = !message; };
  const currentCrop = () => state.crops.get(state.page) || { ...FULL_CROP };
  const updateControls = () => {
    const disabled = state.busy || !state.pdf;
    elements.cropPrevious.disabled = disabled || state.page <= 1;
    elements.cropNext.disabled = disabled || state.page >= (state.pdf?.numPages || 1);
    elements.replaceCropPdf.disabled = state.busy;
    elements.downloadCroppedPdf.disabled = disabled;
  };

  function updateSelection() {
    const crop = currentCrop();
    Object.assign(elements.cropSelection.style, { left: `${crop.x * 100}%`, top: `${crop.y * 100}%`, width: `${crop.width * 100}%`, height: `${crop.height * 100}%` });
    const insets = insetsFromCrop(crop);
    for (const side of ["Top", "Right", "Bottom", "Left"]) {
      elements[`crop${side}`].value = String(insets[side.toLowerCase()]);
      elements[`crop${side}`].parentElement.querySelector("output").value = `${insets[side.toLowerCase()]}%`;
    }
  }

  async function renderPage() {
    if (!state.pdf) return;
    const page = await state.pdf.getPage(state.page);
    const natural = page.getViewport({ scale: 1 });
    const available = Math.max(300, Math.min(820, elements.cropSurface.parentElement.clientWidth - 48));
    const scale = Math.min(1.25, available / natural.width);
    const viewport = await renderPageIntoCanvas(page, elements.cropCanvas, { scale });
    elements.cropSurface.style.width = `${viewport.width}px`;
    elements.cropSurface.style.height = `${viewport.height}px`;
    elements.cropPage.textContent = String(state.page);
    updateSelection();
    updateControls();
  }

  async function loadFile(file) {
    if (!file || state.busy) return;
    state.busy = true; showStatus(); updateControls();
    try {
      const loaded = await openBrowserPdf(file);
      if (state.pdf) await state.pdf.destroy();
      state.file = file; state.bytes = loaded.bytes; state.pdf = loaded.document; state.page = 1; state.crops = new Map();
      elements.cropFileName.textContent = file.name || "document.pdf";
      elements.cropFileDetails.textContent = `${loaded.pageCount} ${loaded.pageCount === 1 ? "page" : "pages"}`;
      elements.cropPageCount.textContent = String(loaded.pageCount);
      elements.cropUploadPanel.hidden = true; elements.cropWorkspace.hidden = false;
      await renderPage();
    } catch (error) { showStatus(pdfErrorMessage(error)); }
    finally { state.busy = false; elements.cropFileInput.value = ""; updateControls(); }
  }

  function updateFromRanges() {
    const values = Object.fromEntries(["top", "right", "bottom", "left"].map(side => [side, Number(elements[`crop${side[0].toUpperCase()}${side.slice(1)}`].value)]));
    state.crops.set(state.page, cropFromInsets(values));
    updateSelection();
  }

  async function changePage(delta) {
    const next = Math.min(state.pdf.numPages, Math.max(1, state.page + delta));
    if (next === state.page) return;
    state.page = next; await renderPage();
  }

  async function download() {
    if (!state.bytes || state.busy) return;
    state.busy = true; showStatus(); elements.cropProgress.hidden = false; elements.cropProgress.textContent = "Applying crop boxes…"; updateControls();
    try {
      const output = await cropPdf(state.bytes, state.crops);
      operationResult.show({ blob: new Blob([output], { type: "application/pdf" }), fileName: croppedPdfName(state.file.name), message: "Your PDF was cropped successfully.", details: [`${state.pdf.numPages} ${state.pdf.numPages === 1 ? "page" : "pages"}`] });
    } catch (error) { showStatus(error?.message || "smallpdf.app could not crop this PDF."); }
    finally { state.busy = false; elements.cropProgress.hidden = true; updateControls(); }
  }

  elements.chooseCropPdf.addEventListener("click", event => { event.stopPropagation(); elements.cropFileInput.click(); });
  elements.replaceCropPdf.addEventListener("click", () => elements.cropFileInput.click());
  elements.cropFileInput.addEventListener("change", () => loadFile(elements.cropFileInput.files?.[0]));
  elements.cropDropZone.addEventListener("click", event => { if (!event.target.closest("button")) elements.cropFileInput.click(); });
  elements.cropDropZone.addEventListener("keydown", event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); elements.cropFileInput.click(); } });
  elements.cropDropZone.addEventListener("dragover", event => { event.preventDefault(); elements.cropDropZone.classList.add("is-dragging"); });
  elements.cropDropZone.addEventListener("dragleave", () => elements.cropDropZone.classList.remove("is-dragging"));
  elements.cropDropZone.addEventListener("drop", event => { event.preventDefault(); elements.cropDropZone.classList.remove("is-dragging"); loadFile(event.dataTransfer.files?.[0]); });
  elements.cropPrevious.addEventListener("click", () => changePage(-1));
  elements.cropNext.addEventListener("click", () => changePage(1));
  for (const side of ["Top", "Right", "Bottom", "Left"]) elements[`crop${side}`].addEventListener("input", updateFromRanges);
  elements.applyCropAll.addEventListener("click", () => { const crop = { ...currentCrop() }; for (let page = 1; page <= state.pdf.numPages; page += 1) state.crops.set(page, crop); showStatus("This crop area now applies to every page."); });
  elements.resetCrop.addEventListener("click", () => { state.crops.delete(state.page); updateSelection(); });
  elements.downloadCroppedPdf.addEventListener("click", download);
  elements.cropSelection.addEventListener("pointerdown", event => {
    const rect = elements.cropSurface.getBoundingClientRect();
    const crop = currentCrop();
    state.dragging = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, rect, crop };
    elements.cropSelection.setPointerCapture(event.pointerId);
  });
  elements.cropSelection.addEventListener("pointermove", event => {
    if (!state.dragging || state.dragging.pointerId !== event.pointerId) return;
    const { startX, startY, rect, crop } = state.dragging;
    state.crops.set(state.page, normalizeCrop({ ...crop, x: crop.x + (event.clientX - startX) / rect.width, y: crop.y + (event.clientY - startY) / rect.height }));
    updateSelection();
  });
  elements.cropSelection.addEventListener("pointerup", () => { state.dragging = null; });
  window.addEventListener("pagehide", () => { operationResult.dispose(); state.pdf?.destroy(); });
  updateControls();
}
